import React, { useState, useEffect, useRef } from 'react';
import {
  Building2,
  Mail,
  UserPlus,
  Send,
  Loader,
  CheckCircle2,
  Users,
  UserCheck,
  UserX,
  Edit2,
  Trash2,
  X as XIcon,
  Check,
  Search,
  ChevronDown,
  ChevronUp,
  ShieldCheck,
  Mail as MailIcon,
  Clock,
} from 'lucide-react';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import {
  useCreateOrganizationMutation as useCreateSuperAdminOrganizationMutation,
  useGetMyOrganizationsQuery,
  useGetRolesQuery,
  MyOrganization,
} from '../../redux/api/authApi';
import {
  useListOrganizationsQuery,
  useGetOrganizationQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
  useGetOrganizationMembersQuery,
  useUpdateOrganizationMemberMutation,
  useRemoveOrganizationMemberMutation,
  useGetOrganizationInvitationsQuery,
  useCreateOrganizationInvitationMutation,
  useRevokeOrganizationInvitationMutation,
} from '../../redux/api/organizationApi';
import {
  setMembers,
  setInvitations,
  addMember,
  updateMember,
  removeMember,
  addInvitation,
  removeInvitation,
  setCurrentOrganization,
} from '../../redux/slices/organizationSlice';
import toast from 'react-hot-toast';
import InviteMemberModal, { InviteFormData } from '../modals/InviteMemberModal';
import { useListPortfoliosQuery } from '../../redux/api/portfolioApi';
import { setActiveTenant, setProfile } from '../../redux/slices/authSlice';
import { useLazyGetMeQuery } from '../../redux/api/authApi';
import type { TenantInfo } from '../../redux/slices/authSlice';

type TabType = 'organization' | 'members' | 'invitations';

// Helper function to extract error message from API error responses
const extractErrorMessage = (err: any, defaultMessage: string = 'An error occurred'): string => {
  if (!err) return defaultMessage;
  
  // Handle Pydantic validation errors (array of error objects)
  if (err?.data?.detail) {
    if (Array.isArray(err.data.detail)) {
      const firstError = err.data.detail[0];
      if (typeof firstError === 'object' && firstError.msg) {
        return firstError.msg;
      } else if (typeof firstError === 'string') {
        return firstError;
      }
    } else if (typeof err.data.detail === 'string') {
      return err.data.detail;
    }
  }
  
  // Handle direct detail property
  if (err?.detail) {
    if (Array.isArray(err.detail)) {
      const firstError = err.detail[0];
      return typeof firstError === 'object' && firstError.msg ? firstError.msg : String(firstError);
    } else if (typeof err.detail === 'string') {
      return err.detail;
    }
  }
  
  // Handle message property
  if (err?.data?.message && typeof err.data.message === 'string') {
    return err.data.message;
  }
  
  if (err?.message && typeof err.message === 'string') {
    return err.message;
  }
  
  return defaultMessage;
};

const AdminViewTab: React.FC = () => {
  const profile = useAppSelector((state) => state.auth.profile);
  const dispatch = useAppDispatch();

  const isSuperAdmin = profile.email?.endsWith('@engramiq.com') ?? false;
  
  // Get tenant_id from new structure (active_tenant) or fallback to legacy (tenant)
  const activeTenant = (profile as any).active_tenant;
  const tenants = ((profile as any).tenants as any[] | null | undefined) || [];
  const hasMultipleTenants = tenants.length > 1;
  const userTenantId = activeTenant?.id || 
    ((profile.tenant as any)?.id || 
     (profile.tenant as any)?.tenant_id || 
     (typeof profile.tenant === 'string' ? profile.tenant : null));
  
  // For super admins: selected organization to manage
  // For org admins: use their own tenant_id (or selected tenant if multiple)
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(userTenantId);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(activeTenant?.id || userTenantId);
  const [showCreateOrgForm, setShowCreateOrgForm] = useState(false);

  const [activeTab, setActiveTab] = useState<TabType>('organization');
  const [searchQuery, setSearchQuery] = useState('');
  const [orgSearchQuery, setOrgSearchQuery] = useState(''); // For organization dropdown search
  const [tenantSearchQuery, setTenantSearchQuery] = useState(''); // For tenant dropdown search
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'org' | 'member' | 'invitation'; id: string; name?: string; token?: string } | null>(null);
  const [editingOrg, setEditingOrg] = useState(false);
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [isOrgDropdownOpen, setIsOrgDropdownOpen] = useState(false);
  const [isTenantDropdownOpen, setIsTenantDropdownOpen] = useState(false);
  const orgDropdownRef = useRef<HTMLDivElement>(null);
  const tenantDropdownRef = useRef<HTMLDivElement>(null);
  
  // Check if user is admin (not super admin)
  // Admin if: has active_tenant with role 'tenant_admin' or 'super_admin' (but not platform super admin)
  // OR has legacy role 'admin' OR has a tenant_id
  const activeTenantRole = activeTenant?.role;
  const isAdmin = !isSuperAdmin && (
    activeTenantRole === 'tenant_admin' || 
    activeTenantRole === 'super_admin' ||
    profile.role === 'admin' || 
    !!userTenantId
  );
  
  // Get my organizations (for invited admins)
  const { data: myOrganizations, refetch: refetchMyOrganizations } = useGetMyOrganizationsQuery(undefined, {
    skip: isSuperAdmin || !isAdmin,
  });
  
  // For invited admins, get the organization from myOrganizations
  const myOrg = myOrganizations?.[0]; // Get first organization if available
  
  // Refetch portfolios
  const { refetch: refetchPortfolios } = useListPortfoliosQuery();

  // Organization form state
  const [orgFormData, setOrgFormData] = useState({
    name: '',
    admin_email: '',
    support_email: '',
  });

  // Invitation form state
  const [inviteFormData, setInviteFormData] = useState({
    email: '',
    role: 'tenant_member',
    expires_in_days: 7,
  });

  // Fetch roles
  const { data: roles = [], isLoading: isLoadingRoles } = useGetRolesQuery();

  // Member edit state
  const [memberEditData, setMemberEditData] = useState({
    role: 'tenant_member',
    is_active: true,
  });

  // Super admin mutation
  const [createSuperAdminOrg, { isLoading: isCreatingSuperAdminOrg }] = useCreateSuperAdminOrganizationMutation();

  // List organizations (for super admins)
  const { data: organizationsData, isLoading: isLoadingOrganizations, refetch: refetchOrganizations } = useListOrganizationsQuery(
    undefined,
    { skip: !isSuperAdmin }
  );

  // For org admins, determine the correct tenant_id to use for fetching organization
  // Use myOrg.id for invited admins, otherwise use selectedTenantId (for admins with multiple tenants) or selectedOrgId or userTenantId
  const orgTenantId = !isSuperAdmin && myOrg?.id 
    ? myOrg.id 
    : (isAdmin && hasMultipleTenants && selectedTenantId 
        ? selectedTenantId 
        : (selectedOrgId || userTenantId));

  // Get selected organization details (for both super admin and org admin)
  // For admins with multiple tenants, use selectedTenantId; otherwise use selectedOrgId or userTenantId
  const shouldSkipOrgQuery = !orgTenantId || (isSuperAdmin && !selectedOrgId);
  const { data: selectedOrgData, isLoading: isLoadingOrgData, error: orgDataError, refetch: refetchOrgData } = useGetOrganizationQuery(
    orgTenantId!,
    { 
      skip: shouldSkipOrgQuery,
      refetchOnMountOrArgChange: true, // Refetch when orgTenantId changes
    }
  );

  // For org admins, also fetch their organization if they have a tenant_id (fallback)
  // Skip this query if we're using selectedTenantId (for admins with multiple tenants)
  const shouldSkipUserOrg = !userTenantId || isSuperAdmin || !!selectedOrgId || !!myOrg?.id || (isAdmin && hasMultipleTenants && !!selectedTenantId);
  const { data: userOrgData, isLoading: isLoadingUserOrg, error: userOrgError, refetch: refetchUserOrg } = useGetOrganizationQuery(
    userTenantId!,
    { 
      skip: shouldSkipUserOrg,
      refetchOnMountOrArgChange: true,
    }
  );
  
  // For org admins, determine the correct tenant_id to use
  // Use myOrg.id for invited admins, otherwise use selectedTenantId (for admins with multiple tenants) or selectedOrgId
  const effectiveTenantId = !isSuperAdmin && myOrg?.id 
    ? myOrg.id 
    : (isAdmin && hasMultipleTenants && selectedTenantId 
        ? selectedTenantId 
        : selectedOrgId);

  // Organization mutations (for org admins)
  const [createOrg, { isLoading: isCreatingOrg }] = useCreateOrganizationMutation();
  const [updateOrg, { isLoading: isUpdatingOrg }] = useUpdateOrganizationMutation();
  const [deleteOrg, { isLoading: isDeletingOrg }] = useDeleteOrganizationMutation();

  // Members queries and mutations
  // Always fetch members (don't skip based on activeTab) so we can refetch after revoking invitations
  const { data: membersData, isLoading: isLoadingMembers, refetch: refetchMembers } = useGetOrganizationMembersQuery(
    { tenant_id: effectiveTenantId!, _limit: 500, _offset: 0 },
    { skip: !effectiveTenantId }
  );

  const [updateMemberMutation, { isLoading: isUpdatingMember }] = useUpdateOrganizationMemberMutation();
  const [removeMemberMutation, { isLoading: isRemovingMember }] = useRemoveOrganizationMemberMutation();

  // Invitations queries and mutations
  // Always fetch invitations (don't skip based on activeTab) so we can refetch after operations
  const { data: invitationsData, isLoading: isLoadingInvitations, refetch: refetchInvitations } =
    useGetOrganizationInvitationsQuery(effectiveTenantId!, { skip: !effectiveTenantId });

  const [createInvitationMutation, { isLoading: isCreatingInvitation }] = useCreateOrganizationInvitationMutation();
  const [revokeInvitationMutation, { isLoading: isRevokingInvitation }] = useRevokeOrganizationInvitationMutation();

  // Sync members to Redux
  useEffect(() => {
    if (membersData) {
      dispatch(setMembers(membersData));
    }
  }, [membersData, dispatch]);

  // Sync invitations to Redux
  useEffect(() => {
    if (invitationsData) {
      dispatch(setInvitations(invitationsData));
    }
  }, [invitationsData, dispatch]);

  // Initialize organization form from selected organization or user's organization
  // Also update when orgTenantId changes (for tenant switching)
  useEffect(() => {
    const orgData = selectedOrgData || userOrgData;
    if (orgData) {
      setOrgFormData({
        name: orgData.name || '',
        admin_email: orgData.admin_email || '',
        support_email: orgData.support_email || '',
      });
    }
  }, [selectedOrgData, userOrgData, orgTenantId]);

  // Set selectedOrgId for org admins when userOrgData is available
  useEffect(() => {
    if (!isSuperAdmin && userTenantId && userOrgData && !selectedOrgId) {
      setSelectedOrgId(userTenantId);
    }
  }, [isSuperAdmin, userTenantId, userOrgData, selectedOrgId]);
  
  // Update selectedOrgId for invited admins when myOrg is available
  useEffect(() => {
    if (!isSuperAdmin && myOrg?.id && selectedOrgId !== myOrg.id) {
      setSelectedOrgId(myOrg.id);
    }
  }, [isSuperAdmin, myOrg?.id, selectedOrgId]);

  // When organization is created or selected, refetch list
  useEffect(() => {
    if (isSuperAdmin && selectedOrgId) {
      refetchOrgData();
    }
  }, [selectedOrgId, isSuperAdmin, refetchOrgData]);

  // For org admins, use their tenant_id (already set in selectedOrgId from userTenantId)
  // Ensure selectedOrgId is set for org admins
  useEffect(() => {
    if (!isSuperAdmin && userTenantId && !selectedOrgId) {
      setSelectedOrgId(userTenantId);
    }
  }, [isSuperAdmin, userTenantId, selectedOrgId]);

  // Initialize selectedTenantId for admins with multiple tenants
  useEffect(() => {
    if (isAdmin && hasMultipleTenants && activeTenant?.id && !selectedTenantId) {
      setSelectedTenantId(activeTenant.id);
      setSelectedOrgId(activeTenant.id);
    }
  }, [isAdmin, hasMultipleTenants, activeTenant?.id, selectedTenantId]);

  // Track previous orgTenantId to detect changes
  const prevOrgTenantIdRef = useRef<string | null>(orgTenantId);
  
  // Refetch organization data when orgTenantId changes (for tenant switching)
  useEffect(() => {
    // Only refetch if orgTenantId actually changed and is valid
    if (orgTenantId && orgTenantId !== prevOrgTenantIdRef.current && !shouldSkipOrgQuery) {
      prevOrgTenantIdRef.current = orgTenantId;
      // Refetch organization data when tenant is switched
      refetchOrgData();
      // Also refetch members and invitations if on those tabs
      if (activeTab === 'members' && effectiveTenantId) {
        refetchMembers();
      }
      if (activeTab === 'invitations' && effectiveTenantId) {
        refetchInvitations();
      }
    } else if (orgTenantId) {
      prevOrgTenantIdRef.current = orgTenantId;
    }
  }, [orgTenantId, shouldSkipOrgQuery, activeTab, effectiveTenantId, refetchOrgData, refetchMembers, refetchInvitations]);

  // Lazy query for refetching profile after tenant switch
  const [triggerGetMe] = useLazyGetMeQuery();

  // Handle tenant switching for admins with multiple tenants
  const handleSwitchTenant = async (tenant: TenantInfo) => {
    try {
      // Update active tenant in Redux immediately for UI responsiveness
      dispatch(setActiveTenant(tenant));
      setSelectedTenantId(tenant.id);
      setSelectedOrgId(tenant.id);
      setIsTenantDropdownOpen(false);
      
      // Refetch profile to get updated data from backend
      const updatedProfile = await triggerGetMe().unwrap();
      
      // Update full profile with backend response
      dispatch(
        setProfile({
          id: updatedProfile.id ?? null,
          email: updatedProfile.email ?? null,
          firstName: updatedProfile.first_name ?? null,
          lastName: updatedProfile.last_name ?? null,
          phone: updatedProfile.phone ?? null,
          role: updatedProfile.role ?? null,
          tenant: updatedProfile.tenant ?? null,
          active_tenant: (updatedProfile as any).active_tenant ?? tenant,
          tenants: (updatedProfile as any).tenants ?? tenants,
          activeSite: updatedProfile.active_site ?? null,
          sessionId: updatedProfile.session_id ?? null,
          permissions: updatedProfile.permissions ?? null,
          isActive: typeof updatedProfile.is_active === 'boolean' ? updatedProfile.is_active : null,
          is_platform_admin: typeof (updatedProfile as any).is_platform_admin === 'boolean' ? (updatedProfile as any).is_platform_admin : null,
        })
      );
      
      // The useEffect hook will handle refetching organization data when selectedTenantId changes
    } catch (error) {
      console.error('Failed to switch tenant:', error);
      toast.error('Failed to switch tenant');
      // Revert the active tenant change on error
      if (activeTenant) {
        dispatch(setActiveTenant(activeTenant));
        setSelectedTenantId(activeTenant.id);
        setSelectedOrgId(activeTenant.id);
      }
    }
  };

  // Handle click outside for organization and tenant dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(event.target as Node)) {
        setIsOrgDropdownOpen(false);
      }
      if (tenantDropdownRef.current && !tenantDropdownRef.current.contains(event.target as Node)) {
        setIsTenantDropdownOpen(false);
      }
    };

    if (isOrgDropdownOpen || isTenantDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOrgDropdownOpen, isTenantDropdownOpen]);

  // Super admin: Create organization
  const handleCreateSuperAdminOrg = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!orgFormData.name.trim()) {
      toast.error('Organization name is required');
      return;
    }

    if (!orgFormData.admin_email.trim()) {
      toast.error('Admin email is required');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(orgFormData.admin_email)) {
      toast.error('Please enter a valid admin email address');
      return;
    }

    if (orgFormData.support_email && !emailRegex.test(orgFormData.support_email)) {
      toast.error('Please enter a valid support email address');
      return;
    }

    try {
      const payload: { name: string; admin_email: string; support_email?: string } = {
        name: orgFormData.name.trim(),
        admin_email: orgFormData.admin_email.trim(),
      };

      if (orgFormData.support_email.trim()) {
        payload.support_email = orgFormData.support_email.trim();
      }

      await createSuperAdminOrg(payload).unwrap();
      toast.success('Organization invitation sent successfully!');
      setOrgFormData({
        name: '',
        admin_email: '',
        support_email: '',
      });
      setShowCreateOrgForm(false);
      refetchOrganizations();
    } catch (err: any) {
      toast.error(extractErrorMessage(err, 'Failed to create organization'));
    }
  };

  // Organization admin: Create organization
  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!orgFormData.name.trim()) {
      toast.error('Organization name is required');
      return;
    }

    if (!orgFormData.admin_email.trim()) {
      toast.error('Admin email is required');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(orgFormData.admin_email)) {
      toast.error('Please enter a valid admin email address');
      return;
    }

    if (orgFormData.support_email && !emailRegex.test(orgFormData.support_email)) {
      toast.error('Please enter a valid support email address');
      return;
    }

    try {
      const payload: { name: string; admin_email: string; support_email?: string } = {
        name: orgFormData.name.trim(),
        admin_email: orgFormData.admin_email.trim(),
      };

      if (orgFormData.support_email.trim()) {
        payload.support_email = orgFormData.support_email.trim();
      }

      const result = await createOrg(payload).unwrap();
      toast.success('Organization created successfully!');
      setOrgFormData({
        name: '',
        admin_email: '',
        support_email: '',
      });
      setEditingOrg(false);
      // Set the newly created organization as selected and refetch
      if (result.id) {
        setSelectedOrgId(result.id);
        refetchUserOrg();
      }
    } catch (err: any) {
      toast.error(extractErrorMessage(err, 'Failed to create organization'));
    }
  };

  // Update organization
  const handleUpdateOrg = async () => {
    if (!selectedOrgId) return;

    try {
      await updateOrg({
        tenant_id: selectedOrgId,
        body: {
          name: orgFormData.name.trim() || undefined,
          admin_email: orgFormData.admin_email.trim() || undefined,
          support_email: orgFormData.support_email.trim() || undefined,
        },
      }).unwrap();
      toast.success('Organization updated successfully!');
      setEditingOrg(false);
      refetchOrgData();
      if (isSuperAdmin) {
        refetchOrganizations();
      }
    } catch (err: any) {
      toast.error(extractErrorMessage(err, 'Failed to update organization'));
    }
  };

  // Delete organization
  const handleDeleteOrg = async () => {
    // For super admin, use selectedOrgId; for admin, use selectedOrgId or userTenantId
    const tenantId = isSuperAdmin ? selectedOrgId : (selectedOrgId || userTenantId);
    if (!tenantId || !deleteConfirm) return;

    try {
      await deleteOrg(tenantId).unwrap();
      toast.success('Organization deleted successfully!');
      setDeleteConfirm(null);
      if (isSuperAdmin) {
        setSelectedOrgId(null);
        refetchOrganizations();
      }
    } catch (err: any) {
      toast.error(extractErrorMessage(err, 'Failed to delete organization'));
    }
  };

  // Update member
  const handleUpdateMember = async (memberId: string) => {
    const tenantId = effectiveTenantId || selectedOrgId;
    if (!tenantId) return;

    try {
      await updateMemberMutation({
        tenant_id: tenantId,
        member_id: memberId,
        body: memberEditData,
      }).unwrap();
      dispatch(updateMember({ id: memberId, ...memberEditData } as any));
      toast.success('Member updated successfully!');
      setEditingMemberId(null);
      refetchMembers();
    } catch (err: any) {
      toast.error(extractErrorMessage(err, 'Failed to update member'));
    }
  };

  // Remove member
  const handleRemoveMember = async () => {
    // For super admin, use selectedOrgId; for admin, use effectiveTenantId
    const tenantId = isSuperAdmin ? selectedOrgId : (effectiveTenantId || selectedOrgId);
    if (!tenantId || !deleteConfirm) return;

    try {
      await removeMemberMutation({
        tenant_id: tenantId,
        member_id: deleteConfirm.id,
      }).unwrap();
      dispatch(removeMember(deleteConfirm.id));
      toast.success('Member removed successfully!');
      setDeleteConfirm(null);
      refetchMembers();
    } catch (err: any) {
      toast.error(extractErrorMessage(err, 'Failed to remove member'));
    }
  };

  // Create invitation
  const handleCreateInvitation = async (data: InviteFormData) => {
    const tenantId = effectiveTenantId || selectedOrgId;
    if (!tenantId) {
      toast.error('No organization selected');
      return;
    }

    if (!data.email.trim()) {
      toast.error('Email is required');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      await createInvitationMutation({
        tenant_id: tenantId,
        body: {
          email: data.email.trim(),
          role: data.role,
          expires_in_days: data.expires_in_days,
        },
      }).unwrap();
      toast.success('Invitation sent successfully!');
      setInviteFormData({
        email: '',
        role: 'member',
        expires_in_days: 7,
      });
      setShowInviteModal(false);
      refetchInvitations();
    } catch (err: any) {
      toast.error(extractErrorMessage(err, 'Failed to create invitation'));
    }
  };

  // Revoke invitation
  const handleRevokeInvitation = async () => {
    const tenantId = effectiveTenantId || selectedOrgId;
    if (!tenantId || !deleteConfirm) return;

    try {
      await revokeInvitationMutation({
        tenant_id: tenantId,
        invitation_id: deleteConfirm.id,
      }).unwrap();
      dispatch(removeInvitation(deleteConfirm.id));
      toast.success('Invitation revoked successfully!');
      setDeleteConfirm(null);
      
      // Refetch both queries to update the UI
      refetchInvitations();
      // Refetch members list to update it (member might have been added after invitation was accepted)
      refetchMembers();
    } catch (err: any) {
      toast.error(extractErrorMessage(err, 'Failed to revoke invitation'));
    }
  };
  

  // Derive member display name - use email from user object or member object
  const getMemberDisplayName = (member: any): string => {
    // Check nested user object first (most common case)
    const user = member?.user;
    if (user?.email) {
      return user.email;
    }
    // Check if email is directly on member object (for pending members)
    if (member?.email) {
      return member.email;
    }
    // Check if user_id is an email (sometimes user_id is the email for pending members)
    if (member?.user_id && typeof member.user_id === 'string' && member.user_id.includes('@')) {
      return member.user_id;
    }
    // Fallback to user_id
    return member.user_id || 'Unknown';
  };

  // Filter members - search by name/email/user_id and role
  const filteredMembers = membersData?.filter((member) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const name = getMemberDisplayName(member).toLowerCase();
    return (
      name.includes(query) ||
      member.role.toLowerCase().includes(query)
    );
  }) || [];
  
  // Check if current user can edit members (must be admin)
  const canEditMembers = isAdmin || isSuperAdmin;

  // Filter invitations
  const filteredInvitations = invitationsData?.filter((invitation) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return invitation.email.toLowerCase().includes(query) || invitation.role.toLowerCase().includes(query);
  }) || [];

  if (isSuperAdmin) {
    // Super Admin View - Organization Selection + Management
    return (
      <div className="w-full h-full flex flex-col bg-[var(--color-bg-primary)]">
        {/* Header */}
        <div className="p-6 border-b border-[var(--color-border)]">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold eg-text-primary">Super Admin</h1>
            <button
              onClick={() => setShowCreateOrgForm(!showCreateOrgForm)}
              className="px-4 py-2 bg-gradient-green-blue hover:bg-engramiq-green rounded-lg text-sm text-engramiq-dark-blue transition-all flex items-center gap-2"
            >
              <UserPlus className="w-4 h-4" />
              {showCreateOrgForm ? 'Cancel' : 'Invite Organization'}
            </button>
          </div>

          {/* Organization Selector with Search */}
          <div className="mb-4">
            <label className="block text-sm font-medium eg-text-secondary mb-2">
              Select Organization to Manage
            </label>
            <div className="relative" ref={orgDropdownRef}>
              <button
                type="button"
                onClick={() => setIsOrgDropdownOpen(!isOrgDropdownOpen)}
                className="w-full px-4 py-2 bg-[var(--color-input-bg)] border border-[var(--color-border)] rounded-lg eg-text-primary focus:outline-none focus:border-engramiq-green transition-all flex items-center justify-between"
              >
                <span>
                  {selectedOrgId
                    ? organizationsData?.find((org) => org.id === selectedOrgId)?.name || 'Select an organization'
                    : '-- Select an organization --'}
                </span>
                {isOrgDropdownOpen ? (
                  <ChevronUp className="w-4 h-4 text-engramiq-green" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-engramiq-green" />
                )}
              </button>
              {isOrgDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-[var(--color-bg-primary)] border border-[var(--color-border)] rounded-lg shadow-lg z-50 max-h-96 overflow-hidden flex flex-col">
                  <div className="p-2 border-b border-[var(--color-border)]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 eg-text-secondary" />
                      <input
                        type="text"
                        placeholder="Search organizations..."
                        value={orgSearchQuery}
                        onChange={(e) => setOrgSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-3 py-2 bg-[var(--color-surface-hover)] rounded eg-text-primary text-sm focus:outline-none focus:border-engramiq-green"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  <div className="overflow-y-auto">
                    {organizationsData
                      ?.filter((org) =>
                        orgSearchQuery === '' ||
                        org.name.toLowerCase().includes(orgSearchQuery.toLowerCase()) ||
                        org.admin_email.toLowerCase().includes(orgSearchQuery.toLowerCase())
                      )
                      .map((org) => (
                        <button
                          key={org.id}
                          onClick={() => {
                            setSelectedOrgId(org.id);
                            // Keep the currently selected organization in Redux so other tabs (e.g. Sites List) can react
                            dispatch(setCurrentOrganization(org));
                            setIsOrgDropdownOpen(false);
                            setOrgSearchQuery('');
                            setActiveTab('organization');
                          }}
                          className={`w-full px-3 py-2 text-left text-sm hover:bg-[var(--color-surface-hover)] transition-colors ${
                            selectedOrgId === org.id
                              ? 'bg-engramiq-green/20 eg-text-primary'
                              : 'eg-text-primary'
                          }`}
                        >
                          <div className="font-medium">{org.name}</div>
                          <div className="text-xs eg-text-secondary">{org.admin_email}</div>
                        </button>
                      ))}
                    {organizationsData?.filter((org) =>
                      orgSearchQuery === '' ||
                      org.name.toLowerCase().includes(orgSearchQuery.toLowerCase()) ||
                      org.admin_email.toLowerCase().includes(orgSearchQuery.toLowerCase())
                    ).length === 0 && (
                      <div className="px-3 py-2 text-sm eg-text-secondary text-center">
                        No organizations found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Invite Organization Form */}
          {showCreateOrgForm && (
            <div className="eg-card-elevated p-6 mb-4">
              <h2 className="text-lg font-semibold eg-text-primary mb-4">Invite New Organization</h2>
              <form onSubmit={handleCreateSuperAdminOrg} className="space-y-4">
              <div>
                <label className="block text-sm font-medium eg-text-primary mb-1">Organization Name *</label>
                <input
                  type="text"
                  value={orgFormData.name}
                  onChange={(e) => setOrgFormData({ ...orgFormData, name: e.target.value })}
                  className="w-full px-3 py-2.5 eg-input"
                  placeholder="Enter organization name"
                  required
                  disabled={isCreatingSuperAdminOrg}
                />
              </div>

              <div>
                <label className="block text-sm font-medium eg-text-primary mb-1">Primary Administrator Email *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-engramiq-green" />
                  <input
                    type="email"
                    value={orgFormData.admin_email}
                    onChange={(e) => setOrgFormData({ ...orgFormData, admin_email: e.target.value })}
                    className="w-full pl-10 pr-3 py-2.5 eg-input"
                    placeholder="admin@example.com"
                    required
                    disabled={isCreatingSuperAdminOrg}
                  />
                </div>
                <p className="text-xs eg-text-secondary mt-1">
                  A welcome email will be sent to this address with signup instructions
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium eg-text-primary mb-1">Support Contact Email (Optional)</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-engramiq-green" />
                  <input
                    type="email"
                    value={orgFormData.support_email}
                    onChange={(e) => setOrgFormData({ ...orgFormData, support_email: e.target.value })}
                    className="w-full pl-10 pr-3 py-2.5 eg-input"
                    placeholder="support@example.com"
                    disabled={isCreatingSuperAdminOrg}
                  />
                </div>
              </div>

              <div className="bg-engramiq-green/10 border border-[var(--color-border)] rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <UserPlus className="w-4 h-4 text-engramiq-green mt-0.5 flex-shrink-0" />
                  <div className="text-xs eg-text-secondary">
                    <p className="font-semibold eg-text-primary mb-1">Important:</p>
                    <ul className="space-y-1 list-disc list-inside">
                      <li>The admin email user must sign up before they can access the organization</li>
                      <li>They will receive a welcome email with signup instructions</li>
                      <li>After signup, they will become the organization owner</li>
                    </ul>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={isCreatingSuperAdminOrg}
                className="w-full py-2.5 rounded-lg font-semibold bg-[#17c480] hover:bg-[#15b375] text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isCreatingSuperAdminOrg ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    Sending invitation...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Send Invitation
                  </>
                )}
              </button>
            </form>
          </div>
          )}

          {/* Tabs - Show when organization is selected */}
          {selectedOrgId && (
            <>
              <div className="flex gap-2 border-b border-[var(--color-border)] mt-4">
                <button
                  onClick={() => setActiveTab('organization')}
                  className={`px-4 py-2 text-sm font-medium transition-all ${
                    activeTab === 'organization'
                      ? 'text-engramiq-green border-b-2 border-engramiq-green'
                      : 'eg-text-secondary hover:text-[var(--color-text-primary)]'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Organization
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('members')}
                  className={`px-4 py-2 text-sm font-medium transition-all ${
                    activeTab === 'members'
                      ? 'text-engramiq-green border-b-2 border-engramiq-green'
                      : 'eg-text-secondary hover:text-[var(--color-text-primary)]'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Members
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('invitations')}
                  className={`px-4 py-2 text-sm font-medium transition-all ${
                    activeTab === 'invitations'
                      ? 'text-engramiq-green border-b-2 border-engramiq-green'
                      : 'eg-text-secondary hover:text-[var(--color-text-primary)]'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <MailIcon className="w-4 h-4" />
                    Invitations
                  </div>
                </button>
              </div>
            </>
          )}
        </div>

        {/* Content */}
        {selectedOrgId ? (
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'organization' && (
              <div className="max-w-2xl mx-auto">
                {isLoadingOrgData ? (
                  <div className="eg-card-elevated p-6">
                    <div className="text-center py-12">
                      <Loader className="w-8 h-8 animate-spin text-engramiq-green mx-auto mb-4" />
                      <p className="eg-text-secondary">Loading organization details...</p>
                    </div>
                  </div>
                ) : orgDataError && orgDataError?.status === 404 ? (
                  <div className="eg-card-elevated p-6">
                    <div className="text-center py-12">
                      <Building2 className="w-16 h-16 text-engramiq-green/50 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold eg-text-primary mb-2">Organization Not Found</h3>
                      <p className="eg-text-secondary">
                        No organization selected.
                        <br />
                        Please select an organization from the dropdown above.
                      </p>
                    </div>
                  </div>
                ) : selectedOrgData ? (
                  <div className="eg-card-elevated p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold eg-text-primary">Organization Details</h2>
                      <div className="flex gap-2">
                        {!editingOrg ? (
                          <>
                            <button
                              onClick={() => setEditingOrg(true)}
                              className="px-3 py-1.5 bg-engramiq-green/20 hover:bg-engramiq-green/30 border border-engramiq-green/50 rounded-lg text-sm text-engramiq-green transition-all flex items-center gap-2"
                            >
                              <Edit2 className="w-4 h-4" />
                              Edit
                            </button>
                            <button
                              onClick={() => setDeleteConfirm({ type: 'org', id: selectedOrgId })}
                              className="px-3 py-1.5 bg-accent-red/20 hover:bg-accent-red/30 border border-accent-red/50 rounded-lg text-sm text-accent-red transition-all flex items-center gap-2"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={handleUpdateOrg}
                              disabled={isUpdatingOrg}
                              className="px-3 py-1.5 bg-engramiq-green hover:bg-engramiq-green/80 rounded-lg text-sm text-white transition-all flex items-center gap-2 disabled:opacity-50"
                            >
                              <Check className="w-4 h-4" />
                              Save
                            </button>
                            <button
                              onClick={() => {
                                setEditingOrg(false);
                                if (selectedOrgData) {
                                  setOrgFormData({
                                    name: selectedOrgData.name || '',
                                    admin_email: selectedOrgData.admin_email || '',
                                    support_email: selectedOrgData.support_email || '',
                                  });
                                }
                              }}
                              className="px-3 py-1.5 bg-[var(--color-surface)] hover:bg-[var(--color-surface-hover)] rounded-lg text-sm eg-text-primary transition-all flex items-center gap-2"
                            >
                              <XIcon className="w-4 h-4" />
                              Cancel
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium eg-text-secondary mb-1">Organization Name</label>
                        <input
                          type="text"
                          value={orgFormData.name}
                          onChange={(e) => setOrgFormData({ ...orgFormData, name: e.target.value })}
                          disabled={!editingOrg}
                          className="w-full px-3 py-2 eg-input disabled:opacity-50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium eg-text-secondary mb-1">Admin Email</label>
                        <input
                          type="email"
                          value={orgFormData.admin_email}
                          onChange={(e) => setOrgFormData({ ...orgFormData, admin_email: e.target.value })}
                          disabled={!editingOrg}
                          className="w-full px-3 py-2 eg-input disabled:opacity-50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium eg-text-secondary mb-1">Support Email</label>
                        <input
                          type="email"
                          value={orgFormData.support_email}
                          onChange={(e) => setOrgFormData({ ...orgFormData, support_email: e.target.value })}
                          disabled={!editingOrg}
                          className="w-full px-3 py-2 eg-input disabled:opacity-50"
                        />
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            )}

            {activeTab === 'members' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex-1 max-w-md">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 eg-text-secondary" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search members..."
                        className="pl-10 pr-3 py-2 bg-[var(--color-input-bg)] border border-[var(--color-border)] rounded-lg text-sm eg-text-primary focus:outline-none focus:border-engramiq-green transition-all w-full"
                      />
                    </div>
                  </div>
                </div>

                {isLoadingMembers ? (
                  <div className="text-center py-12">
                    <Loader className="w-8 h-8 animate-spin text-engramiq-green mx-auto mb-4" />
                    <p className="eg-text-secondary">Loading members...</p>
                  </div>
                ) : filteredMembers.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="eg-text-secondary">No members found</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredMembers.map((member) => (
                      <div
                        key={member.id}
                        className="eg-card p-4"
                      >
                        {editingMemberId === member.id ? (
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-medium eg-text-secondary mb-1">Role</label>
                              <div className="relative">
                                <select
                                  value={memberEditData.role}
                                  onChange={(e) => setMemberEditData({ ...memberEditData, role: e.target.value })}
                                  className="w-full px-3 py-2 pr-10 eg-input appearance-none"
                                  disabled={isLoadingRoles}
                                >
                                  {isLoadingRoles ? (
                                    <option>Loading roles...</option>
                                  ) : roles.length === 0 ? (
                                    <>
                                      <option value="tenant_member">TENANT_MEMBER</option>
                                      <option value="tenant_admin">TENANT_ADMIN</option>
                                    </>
                                  ) : (
                                    roles.map((role) => (
                                      <option key={role.value} value={role.value}>
                                        {role.label}
                                      </option>
                                    ))
                                  )}
                                </select>
                                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 eg-text-primary pointer-events-none" />
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <label className="flex items-center gap-2 text-sm eg-text-secondary">
                                <input
                                  type="checkbox"
                                  checked={memberEditData.is_active}
                                  onChange={(e) => setMemberEditData({ ...memberEditData, is_active: e.target.checked })}
                                  className="rounded"
                                />
                                Active
                              </label>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleUpdateMember(member.id)}
                                disabled={isUpdatingMember}
                                className="px-3 py-1.5 bg-engramiq-green hover:bg-engramiq-green/80 rounded-lg text-sm text-white transition-all flex items-center gap-2 disabled:opacity-50"
                              >
                                <Check className="w-4 h-4" />
                                Save
                              </button>
                              <button
                                onClick={() => setEditingMemberId(null)}
                                className="px-3 py-1.5 bg-[var(--color-surface)] hover:bg-[var(--color-surface-hover)] rounded-lg text-sm eg-text-primary transition-all flex items-center gap-2"
                              >
                                <XIcon className="w-4 h-4" />
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="eg-text-primary font-medium">{getMemberDisplayName(member)}</p>
                              <div className="flex gap-4 text-xs eg-text-secondary mt-1">
                                {canEditMembers && (
                                  <span className="flex items-center gap-1">
                                    <ShieldCheck className="w-3 h-3" />
                                    {member.role}
                                  </span>
                                )}
                                <span className="flex items-center gap-1">
                                  {member.is_active ? (
                                    <UserCheck className="w-3 h-3 text-engramiq-green" />
                                  ) : (
                                    <UserX className="w-3 h-3 text-accent-red" />
                                  )}
                                  {member.is_active ? 'Active' : 'Inactive'}
                                </span>
                                <span>
                                  Joined: {new Date(member.joined_at).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                            {canEditMembers && (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => {
                                    setEditingMemberId(member.id);
                                    setMemberEditData({
                                      role: member.role,
                                      is_active: member.is_active,
                                    });
                                  }}
                                  className="p-2 bg-engramiq-green/20 hover:bg-engramiq-green/30 border border-engramiq-green/50 rounded-lg text-engramiq-green transition-all"
                                  title="Edit"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => setDeleteConfirm({ type: 'member', id: member.id, name: getMemberDisplayName(member) })}
                                  className="p-2 bg-accent-red/20 hover:bg-accent-red/30 border border-accent-red/50 rounded-lg text-accent-red transition-all"
                                  title="Remove"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'invitations' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex-1 max-w-md">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 eg-text-secondary" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search invitations..."
                        className="pl-10 pr-3 py-2 bg-[var(--color-input-bg)] border border-[var(--color-border)] rounded-lg text-sm eg-text-primary focus:outline-none focus:border-engramiq-green transition-all w-full"
                      />
                    </div>
                  </div>
                    <button
                      onClick={() => {
                        setShowInviteModal(true);
                      }}
                      className="px-4 py-2 bg-gradient-green-blue hover:bg-engramiq-green rounded-lg text-sm text-engramiq-dark-blue transition-all flex items-center gap-2"
                    >
                    <UserPlus className="w-4 h-4" />
                    Invite Member
                  </button>
                </div>

                {isLoadingInvitations ? (
                  <div className="text-center py-12">
                    <Loader className="w-8 h-8 animate-spin text-engramiq-green mx-auto mb-4" />
                    <p className="eg-text-secondary">Loading invitations...</p>
                  </div>
                ) : filteredInvitations.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="eg-text-secondary">No invitations found</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredInvitations.map((invitation) => (
                      <div
                        key={invitation.id}
                        className="eg-card p-4"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="eg-text-primary font-medium">{invitation.email}</p>
                            <div className="flex gap-4 text-xs eg-text-secondary mt-1">
                              <span className="flex items-center gap-1">
                                <ShieldCheck className="w-3 h-3" />
                                {invitation.role}
                              </span>
                              {invitation.accepted_at ? (
                                <span className="flex items-center gap-1 text-engramiq-green">
                                  <CheckCircle2 className="w-3 h-3" />
                                  Accepted
                                </span>
                              ) : (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  Pending
                                </span>
                              )}
                              <span>
                                Expires: {new Date(invitation.expires_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          {!invitation.accepted_at && (
                            <button
                              onClick={() => setDeleteConfirm({ type: 'invitation', id: invitation.id, name: invitation.email })}
                              className="p-2 bg-accent-red/20 hover:bg-accent-red/30 border border-accent-red/50 rounded-lg text-accent-red transition-all"
                              title="Revoke"
                            >
                              <XIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6 flex items-center justify-center">
            <div className="text-center">
              <Building2 className="w-16 h-16 text-engramiq-green/50 mx-auto mb-4" />
              <p className="eg-text-secondary text-lg">Select an organization to manage</p>
            </div>
          </div>
        )}

        {/* Invite Modal */}
        <InviteMemberModal
          isOpen={showInviteModal}
          onClose={() => {
            setShowInviteModal(false);
          }}
          onSubmit={handleCreateInvitation}
          isLoading={isCreatingInvitation}
          initialData={inviteFormData}
        />
        
        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="eg-modal p-6 max-w-md mx-4">
              <h3 className="text-xl font-bold eg-text-primary mb-4">
                {deleteConfirm.type === 'org' && 'Delete Organization'}
                {deleteConfirm.type === 'member' && 'Remove Member'}
                {deleteConfirm.type === 'invitation' && 'Revoke Invitation'}
              </h3>
              <p className="eg-text-secondary mb-6">
                Are you sure you want to{' '}
                {deleteConfirm.type === 'org' && 'delete this organization?'}
                {deleteConfirm.type === 'member' && `remove ${deleteConfirm.name}?`}
                {deleteConfirm.type === 'invitation' && `revoke the invitation for ${deleteConfirm.name}?`}
                {' '}This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  disabled={isDeletingOrg || isRemovingMember || isRevokingInvitation}
                  className="px-4 py-2 bg-[var(--color-surface)] border border-[var(--color-border)] hover:border-engramiq-green rounded-lg eg-text-primary transition-all disabled:opacity-50"
                >
                  No
                </button>
                <button
                  onClick={() => {
                    if (deleteConfirm.type === 'org') handleDeleteOrg();
                    else if (deleteConfirm.type === 'member') handleRemoveMember();
                    else if (deleteConfirm.type === 'invitation') handleRevokeInvitation();
                  }}
                  disabled={isDeletingOrg || isRemovingMember || isRevokingInvitation}
                  className="px-4 py-2 bg-accent-red hover:bg-accent-red/80 border border-accent-red rounded-lg text-white transition-all disabled:opacity-50 flex items-center gap-2"
                >
                  {(isDeletingOrg || isRemovingMember || isRevokingInvitation) ? (
                    <>
                      <Loader className="animate-spin" size={16} />
                      Processing...
                    </>
                  ) : (
                    'Yes'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Organization Admin View - Management UI
  // For invited admins, use the organization id from myOrganizations
  // For regular admins, use effectiveTenantId (which uses selectedOrgId, which is initialized from userTenantId)
  const adminOrgId = effectiveTenantId || myOrg?.id || userTenantId;
  
  // Only show error if user is an admin but has no organization
  // Super admins should never see this error
  // Also check if we're still loading organization data before showing error
  if (!isSuperAdmin && !adminOrgId && !myOrg && isAdmin && !isLoadingUserOrg && !isLoadingOrgData) {
    return (
      <div className="h-full w-full overflow-auto p-6" style={{ background: 'var(--color-zone-recessed)' }}>
        <div className="max-w-2xl mx-auto">
          <div className="eg-card-elevated p-6">
            <div className="flex items-center gap-3 mb-4">
              <Building2 className="w-6 h-6 text-engramiq-green" />
              <h2 className="text-xl font-semibold eg-text-primary">Admin View</h2>
            </div>
            <p className="eg-text-secondary mb-4">
              You need to be part of an organization to access this section.
            </p>
            <button
              onClick={() => setEditingOrg(true)}
              className="px-4 py-2 bg-gradient-green-blue hover:bg-engramiq-green rounded-lg text-sm text-engramiq-dark-blue transition-all flex items-center gap-2"
            >
              <UserPlus className="w-4 h-4" />
              Invite Organization
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="eg-page-shell">
      {/* Header */}
      <div className="eg-toolbar-header p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold eg-text-primary">Organization Management</h1>
            {myOrg && (
              <p className="text-sm eg-text-secondary mt-1">{myOrg.name}</p>
            )}
          </div>
        </div>

        {/* Tenant Selector for Admins with Multiple Tenants */}
        {isAdmin && hasMultipleTenants && (
          <div className="mb-4">
            <label className="block text-sm font-medium eg-text-secondary mb-2">
              Select Tenant to Manage
            </label>
            <div className="relative" ref={tenantDropdownRef}>
              <button
                type="button"
                onClick={() => setIsTenantDropdownOpen(!isTenantDropdownOpen)}
                className="w-full px-4 py-2 bg-[var(--color-input-bg)] border border-[var(--color-border)] rounded-lg eg-text-primary focus:outline-none focus:border-engramiq-green transition-all flex items-center justify-between"
              >
                <span>
                  {selectedTenantId
                    ? tenants.find((t) => t.id === selectedTenantId)?.name || 'Select a tenant'
                    : '-- Select a tenant --'}
                </span>
                {isTenantDropdownOpen ? (
                  <ChevronUp className="w-4 h-4 text-engramiq-green" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-engramiq-green" />
                )}
              </button>
              {isTenantDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-[var(--color-bg-primary)] border border-[var(--color-border)] rounded-lg shadow-lg z-50 max-h-96 overflow-hidden flex flex-col">
                  <div className="p-2 border-b border-[var(--color-border)]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 eg-text-secondary" />
                      <input
                        type="text"
                        placeholder="Search tenants..."
                        value={tenantSearchQuery}
                        onChange={(e) => setTenantSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-3 py-2 bg-[var(--color-surface-hover)] rounded eg-text-primary text-sm focus:outline-none focus:border-engramiq-green"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  <div className="overflow-y-auto">
                    {tenants
                      .filter((tenant) =>
                        tenantSearchQuery === '' ||
                        tenant.name.toLowerCase().includes(tenantSearchQuery.toLowerCase()) ||
                        tenant.role.toLowerCase().includes(tenantSearchQuery.toLowerCase())
                      )
                      .map((tenant) => (
                        <button
                          key={tenant.id}
                          onClick={() => {
                            handleSwitchTenant(tenant);
                          }}
                          className={`w-full px-3 py-2 text-left text-sm hover:bg-[var(--color-surface-hover)] transition-colors ${
                            selectedTenantId === tenant.id
                              ? 'bg-engramiq-green/20 eg-text-primary'
                              : 'eg-text-primary'
                          }`}
                        >
                          <div className="font-medium">{tenant.name}</div>
                          <div className="text-xs eg-text-secondary">{tenant.role}</div>
                        </button>
                      ))}
                    {tenants.filter((tenant) =>
                      tenantSearchQuery === '' ||
                      tenant.name.toLowerCase().includes(tenantSearchQuery.toLowerCase()) ||
                      tenant.role.toLowerCase().includes(tenantSearchQuery.toLowerCase())
                    ).length === 0 && (
                      <div className="px-3 py-2 text-sm eg-text-secondary text-center">
                        No tenants found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b border-[var(--color-border)]">
          <button
            onClick={() => setActiveTab('organization')}
            className={`px-4 py-2 text-sm font-medium transition-all ${
              activeTab === 'organization'
                ? 'text-engramiq-green border-b-2 border-engramiq-green'
                : 'eg-text-secondary hover:text-[var(--color-text-primary)]'
            }`}
          >
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Organization
            </div>
          </button>
          <button
            onClick={() => setActiveTab('members')}
            className={`px-4 py-2 text-sm font-medium transition-all ${
              activeTab === 'members'
                ? 'text-engramiq-green border-b-2 border-engramiq-green'
                : 'eg-text-secondary hover:text-[var(--color-text-primary)]'
            }`}
          >
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Members
            </div>
          </button>
          <button
            onClick={() => setActiveTab('invitations')}
            className={`px-4 py-2 text-sm font-medium transition-all ${
              activeTab === 'invitations'
                ? 'text-engramiq-green border-b-2 border-engramiq-green'
                : 'eg-text-secondary hover:text-[var(--color-text-primary)]'
            }`}
          >
            <div className="flex items-center gap-2">
              <MailIcon className="w-4 h-4" />
              Invitations
            </div>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="eg-page-content p-6">
        {activeTab === 'organization' && (
          <div className="max-w-2xl mx-auto">
            {isLoadingOrgData || isLoadingUserOrg ? (
              <div className="eg-card-elevated p-6">
                <div className="text-center py-12">
                  <Loader className="w-8 h-8 animate-spin text-engramiq-green mx-auto mb-4" />
                  <p className="eg-text-secondary">Loading organization details...</p>
                </div>
              </div>
            ) : (orgDataError || userOrgError) && (orgDataError?.status === 404 || userOrgError?.status === 404) ? (
              <div className="eg-card-elevated p-6">
                <div className="text-center py-12">
                  <Building2 className="w-16 h-16 text-engramiq-green/50 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold eg-text-primary mb-2">No Organization Found</h3>
                  <p className="eg-text-secondary mb-6">
                    {myOrg ? `You are part of ${myOrg.name}` : "You don't have an organization yet. Create one to get started."}
                  </p>
                  {!myOrg && (
                    <button
                      onClick={() => setEditingOrg(true)}
                      className="px-4 py-2 bg-gradient-green-blue hover:bg-engramiq-green rounded-lg text-sm text-engramiq-dark-blue transition-all flex items-center gap-2 mx-auto"
                    >
                      <UserPlus className="w-4 h-4" />
                      Create Organization
                    </button>
                  )}
                </div>
              </div>
            ) : editingOrg && !(selectedOrgData || userOrgData) && !myOrg ? (
              <div className="eg-card-elevated p-6">
                <h2 className="text-lg font-semibold eg-text-primary mb-4">Create Organization</h2>
                <form onSubmit={handleCreateOrg} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium eg-text-secondary mb-1">Organization Name *</label>
                    <input
                      type="text"
                      value={orgFormData.name}
                      onChange={(e) => setOrgFormData({ ...orgFormData, name: e.target.value })}
                      className="w-full px-3 py-2 eg-input"
                      placeholder="Enter organization name"
                      required
                      disabled={isCreatingOrg}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium eg-text-secondary mb-1">Admin Email *</label>
                    <input
                      type="email"
                      value={orgFormData.admin_email}
                      onChange={(e) => setOrgFormData({ ...orgFormData, admin_email: e.target.value })}
                      className="w-full px-3 py-2 eg-input"
                      placeholder="admin@example.com"
                      required
                      disabled={isCreatingOrg}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium eg-text-secondary mb-1">Support Email (Optional)</label>
                    <input
                      type="email"
                      value={orgFormData.support_email}
                      onChange={(e) => setOrgFormData({ ...orgFormData, support_email: e.target.value })}
                      className="w-full px-3 py-2 eg-input"
                      placeholder="support@example.com"
                      disabled={isCreatingOrg}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={isCreatingOrg}
                      className="px-4 py-2 bg-engramiq-green hover:bg-engramiq-green/80 rounded-lg text-sm text-white transition-all flex items-center gap-2 disabled:opacity-50"
                    >
                      {isCreatingOrg ? (
                        <>
                          <Loader className="animate-spin" size={16} />
                          Creating...
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4" />
                          Create
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingOrg(false);
                        setOrgFormData({
                          name: '',
                          admin_email: '',
                          support_email: '',
                        });
                      }}
                      className="px-4 py-2 bg-[var(--color-surface)] hover:bg-[var(--color-surface-hover)] rounded-lg text-sm eg-text-primary transition-all flex items-center gap-2"
                    >
                      <XIcon className="w-4 h-4" />
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            ) : (selectedOrgData || userOrgData) ? (
              <div className="eg-card-elevated p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold eg-text-primary">Organization Details</h2>
                  <div className="flex gap-2">
                    {!editingOrg ? (
                      <>
                        <button
                          onClick={() => setEditingOrg(true)}
                          className="px-3 py-1.5 bg-engramiq-green/20 hover:bg-engramiq-green/30 border border-engramiq-green/50 rounded-lg text-sm text-engramiq-green transition-all flex items-center gap-2"
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleteConfirm({ type: 'org', id: selectedOrgId || userTenantId! })}
                          className="px-3 py-1.5 bg-accent-red/20 hover:bg-accent-red/30 border border-accent-red/50 rounded-lg text-sm text-accent-red transition-all flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={handleUpdateOrg}
                          disabled={isUpdatingOrg}
                          className="px-3 py-1.5 bg-engramiq-green hover:bg-engramiq-green/80 rounded-lg text-sm text-white transition-all flex items-center gap-2 disabled:opacity-50"
                        >
                          <Check className="w-4 h-4" />
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setEditingOrg(false);
                            const orgData = selectedOrgData || userOrgData;
                            if (orgData) {
                              setOrgFormData({
                                name: orgData.name || '',
                                admin_email: orgData.admin_email || '',
                                support_email: orgData.support_email || '',
                              });
                            }
                          }}
                          className="px-3 py-1.5 bg-[var(--color-surface)] hover:bg-[var(--color-surface-hover)] rounded-lg text-sm eg-text-primary transition-all flex items-center gap-2"
                        >
                          <XIcon className="w-4 h-4" />
                          Cancel
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium eg-text-secondary mb-1">Organization Name</label>
                    <input
                      type="text"
                      value={orgFormData.name}
                      onChange={(e) => setOrgFormData({ ...orgFormData, name: e.target.value })}
                      disabled={!editingOrg}
                      className="w-full px-3 py-2 eg-input disabled:opacity-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium eg-text-secondary mb-1">Admin Email</label>
                    <input
                      type="email"
                      value={orgFormData.admin_email}
                      onChange={(e) => setOrgFormData({ ...orgFormData, admin_email: e.target.value })}
                      disabled={!editingOrg}
                      className="w-full px-3 py-2 eg-input disabled:opacity-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium eg-text-secondary mb-1">Support Email</label>
                    <input
                      type="email"
                      value={orgFormData.support_email}
                      onChange={(e) => setOrgFormData({ ...orgFormData, support_email: e.target.value })}
                      disabled={!editingOrg}
                      className="w-full px-3 py-2 eg-input disabled:opacity-50"
                    />
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        )}

        {activeTab === 'members' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 eg-text-secondary" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search members..."
                    className="pl-10 pr-3 py-2 bg-[var(--color-input-bg)] border border-[var(--color-border)] rounded-lg text-sm eg-text-primary focus:outline-none focus:border-engramiq-green transition-all w-full"
                  />
                </div>
              </div>
            </div>

            {isLoadingMembers ? (
              <div className="text-center py-12">
                <Loader className="w-8 h-8 animate-spin text-engramiq-green mx-auto mb-4" />
                <p className="eg-text-secondary">Loading members...</p>
              </div>
            ) : filteredMembers.length === 0 ? (
              <div className="text-center py-12">
                <p className="eg-text-secondary">No members found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredMembers.map((member) => (
                  <div
                    key={member.id}
                    className="eg-card p-4"
                  >
                    {editingMemberId === member.id ? (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium eg-text-secondary mb-1">Role</label>
                          <div className="relative">
                            <select
                              value={memberEditData.role}
                              onChange={(e) => setMemberEditData({ ...memberEditData, role: e.target.value })}
                              className="w-full px-3 py-2 pr-10 eg-input appearance-none"
                              disabled={isLoadingRoles}
                            >
                              {isLoadingRoles ? (
                                <option>Loading roles...</option>
                              ) : roles.length === 0 ? (
                                <>
                                  <option value="tenant_member">TENANT_MEMBER</option>
                                  <option value="tenant_admin">TENANT_ADMIN</option>
                                </>
                              ) : (
                                roles.map((role) => (
                                  <option key={role.value} value={role.value}>
                                    {role.label}
                                  </option>
                                ))
                              )}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 eg-text-primary pointer-events-none" />
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="flex items-center gap-2 text-sm eg-text-secondary">
                            <input
                              type="checkbox"
                              checked={memberEditData.is_active}
                              onChange={(e) => setMemberEditData({ ...memberEditData, is_active: e.target.checked })}
                              className="rounded"
                            />
                            Active
                          </label>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleUpdateMember(member.id)}
                            disabled={isUpdatingMember}
                            className="px-3 py-1.5 bg-engramiq-green hover:bg-engramiq-green/80 rounded-lg text-sm text-white transition-all flex items-center gap-2 disabled:opacity-50"
                          >
                            <Check className="w-4 h-4" />
                            Save
                          </button>
                          <button
                            onClick={() => setEditingMemberId(null)}
                            className="px-3 py-1.5 bg-[var(--color-surface)] hover:bg-[var(--color-surface-hover)] rounded-lg text-sm eg-text-primary transition-all flex items-center gap-2"
                          >
                            <XIcon className="w-4 h-4" />
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="eg-text-primary font-medium">{getMemberDisplayName(member)}</p>
                          <div className="flex gap-4 text-xs eg-text-secondary mt-1">
                            <span className="flex items-center gap-1">
                              <ShieldCheck className="w-3 h-3" />
                              {member.role}
                            </span>
                            <span className="flex items-center gap-1">
                              {member.is_active ? (
                                <UserCheck className="w-3 h-3 text-engramiq-green" />
                              ) : (
                                <UserX className="w-3 h-3 text-accent-red" />
                              )}
                              {member.is_active ? 'Active' : 'Inactive'}
                            </span>
                            <span>
                              Joined: {new Date(member.joined_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setEditingMemberId(member.id);
                              setMemberEditData({
                                role: member.role,
                                is_active: member.is_active,
                              });
                            }}
                            className="p-2 bg-engramiq-green/20 hover:bg-engramiq-green/30 border border-engramiq-green/50 rounded-lg text-engramiq-green transition-all"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm({ type: 'member', id: member.id, name: getMemberDisplayName(member) })}
                            className="p-2 bg-accent-red/20 hover:bg-accent-red/30 border border-accent-red/50 rounded-lg text-accent-red transition-all"
                            title="Remove"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'invitations' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 eg-text-secondary" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search invitations..."
                    className="pl-10 pr-3 py-2 bg-[var(--color-input-bg)] border border-[var(--color-border)] rounded-lg text-sm eg-text-primary focus:outline-none focus:border-engramiq-green transition-all w-full"
                  />
                </div>
              </div>
              <button
                onClick={() => setShowInviteModal(true)}
                className="px-4 py-2 bg-gradient-green-blue hover:bg-engramiq-green rounded-lg text-sm text-engramiq-dark-blue transition-all flex items-center gap-2"
              >
                <UserPlus className="w-4 h-4" />
                Invite Member
              </button>
            </div>

            {isLoadingInvitations ? (
              <div className="text-center py-12">
                <Loader className="w-8 h-8 animate-spin text-engramiq-green mx-auto mb-4" />
                <p className="eg-text-secondary">Loading invitations...</p>
              </div>
            ) : filteredInvitations.length === 0 ? (
              <div className="text-center py-12">
                <p className="eg-text-secondary">No invitations found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredInvitations.map((invitation) => (
                  <div
                    key={invitation.id}
                    className="eg-card p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="eg-text-primary font-medium">{invitation.email}</p>
                        <div className="flex gap-4 text-xs eg-text-secondary mt-1">
                          <span className="flex items-center gap-1">
                            <ShieldCheck className="w-3 h-3" />
                            {invitation.role}
                          </span>
                          {invitation.accepted_at ? (
                            <span className="flex items-center gap-1 text-engramiq-green">
                              <CheckCircle2 className="w-3 h-3" />
                              Accepted
                            </span>
                          ) : (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Pending
                            </span>
                          )}
                          <span>
                            Expires: {new Date(invitation.expires_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      {!invitation.accepted_at && (
                        <button
                          onClick={() => setDeleteConfirm({ type: 'invitation', id: invitation.id, name: invitation.email })}
                          className="p-2 bg-accent-red/20 hover:bg-accent-red/30 border border-accent-red/50 rounded-lg text-accent-red transition-all"
                          title="Revoke"
                        >
                          <XIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Invite Modal */}
      <InviteMemberModal
        isOpen={showInviteModal}
        onClose={() => {
          setShowInviteModal(false);
        }}
        onSubmit={handleCreateInvitation}
        isLoading={isCreatingInvitation}
        initialData={inviteFormData}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="eg-modal p-6 max-w-md mx-4">
            <h3 className="text-xl font-bold eg-text-primary mb-4">
              {deleteConfirm.type === 'org' && 'Delete Organization'}
              {deleteConfirm.type === 'member' && 'Remove Member'}
              {deleteConfirm.type === 'invitation' && 'Revoke Invitation'}
            </h3>
            <p className="eg-text-secondary mb-6">
              Are you sure you want to{' '}
              {deleteConfirm.type === 'org' && 'delete this organization?'}
              {deleteConfirm.type === 'member' && `remove ${deleteConfirm.name}?`}
              {deleteConfirm.type === 'invitation' && `revoke the invitation for ${deleteConfirm.name}?`}
              {' '}This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={isDeletingOrg || isRemovingMember || isRevokingInvitation}
                className="px-4 py-2 bg-[var(--color-surface)] border border-[var(--color-border)] hover:border-engramiq-green rounded-lg eg-text-primary transition-all disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (deleteConfirm.type === 'org') handleDeleteOrg();
                  else if (deleteConfirm.type === 'member') handleRemoveMember();
                  else if (deleteConfirm.type === 'invitation') handleRevokeInvitation();
                }}
                disabled={isDeletingOrg || isRemovingMember || isRevokingInvitation}
                className="px-4 py-2 bg-accent-red hover:bg-accent-red/80 border border-accent-red rounded-lg text-white transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {(isDeletingOrg || isRemovingMember || isRevokingInvitation) ? (
                  <>
                    <Loader className="animate-spin" size={16} />
                    Processing...
                  </>
                ) : (
                  'Confirm'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminViewTab;
